import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Lock, TrendingUp } from "lucide-react";

export const RESULT = {
  tp2: { t: "TP2 Hit", c: "st-win" }, breakeven: { t: "TP1 Hit", c: "st-win" },
  sl: { t: "Stopped Out", c: "st-loss" }, expired: { t: "Expired", c: "st-mute" },
};
const fmt = (v, d) => (typeof v === "number" ? v.toFixed(d) : "—");
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");

export const useResults = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/scanner/results").then(({ data: d }) => setData(d)).catch(() => setData({ error: true })); }, []);
  return data;
};

export const ResultRow = ({ s }) => {
  const r = RESULT[s.status] || RESULT.expired;
  return (
    <div className="res-row" data-testid="results-row">
      <span className="res-date">{day(s.closed_at || s.ts)}</span>
      <b className="res-pair">{s.label}</b>
      <span className={s.side === "BUY" ? "up" : "dn"}>{s.side}</span>
      <span className="res-num hide-sm">{fmt(s.entry, s.dec)}</span>
      <span className="res-num hide-sm">{fmt(s.sl, s.dec)}</span>
      <span className="res-num hide-sm">{fmt(s.tp1, s.dec)}</span>
      <span className="res-num hide-sm">{fmt(s.tp2, s.dec)}</span>
      <span className={`sc-status ${r.c}`}>{r.t}</span>
    </div>
  );
};

const Results = () => {
  const data = useResults();
  const nav = useNavigate();
  const a = data?.all_time, m = data?.last_30d;
  const cards = [
    ["Win Rate · All Time", a?.win_rate != null ? `${a.win_rate}%` : "—", a ? `${a.wins} wins · ${a.losses} losses` : ""],
    ["Win Rate · 30 Days", m?.win_rate != null ? `${m.win_rate}%` : "—", m ? `${m.closed} closed signals` : ""],
    ["TP2 Hits", a ? a.tp2 : "—", "Full target reached"],
    ["Live Right Now", data?.live_count ?? "—", "Members only"],
  ];
  return (
    <div className="admin" data-testid="results-page">
      <div className="admin-wrap">
        <div className="section-eye left"><TrendingUp size={11} /> Public Track Record</div>
        <h2 className="section-h left">Every Signal.<br /><em>Every Result.</em></h2>
        <p className="section-sub left">Every closed AI Scanner signal is listed here: wins, losses and expired. Nothing is hidden. Live signals stay members-only until they close.</p>
        <div className="admin-cards">
          {cards.map(([l, v, sub]) => (
            <div className="live-card admin-card" key={l} data-testid={`results-stat-${l.split(" ")[0].toLowerCase()}-${l.includes("30") ? "30d" : "all"}`}>
              <div className="lc-eye">{l}</div><div className="ac-val">{v}</div><div className="ac-sub">{sub}</div>
            </div>
          ))}
        </div>
        <div className="live-card res-lock" data-testid="results-live-locked">
          <Lock size={14} /> <span>{data?.live_count || 0} signal{data?.live_count === 1 ? "" : "s"} running right now. Members see the entry, stop and targets live.</span>
          <button className="mini-btn" onClick={() => nav("/#pricing")} data-testid="results-unlock-btn">Unlock Live Signals →</button>
        </div>
        <div className="live-card">
          <div className="res-row res-head"><span>Closed</span><span>Market</span><span>Side</span><span className="hide-sm">Entry</span><span className="hide-sm">Stop</span><span className="hide-sm">TP1</span><span className="hide-sm">TP2</span><span>Result</span></div>
          {!data && <div className="sig-wait">Loading…</div>}
          {data && !data.error && data.signals.length === 0 && <div className="sig-wait" data-testid="results-empty">No closed signals yet. The scanner started recently, so results will appear here as signals close.</div>}
          {data?.error && <div className="sig-wait">Results are temporarily unavailable.</div>}
          {(data?.signals || []).map((s) => <ResultRow key={s.id} s={s} />)}
        </div>
        <p className="live-disc">Win rate = signals that reached TP1 or TP2 divided by wins plus stop-outs. Expired signals are listed but not counted. Results are hypothetical: they are based on 1-hour candle highs and lows and do not include spreads, fees or slippage. Past results do not guarantee future results. Not financial advice.</p>
      </div>
    </div>
  );
};

export default Results;
